import React, { useState, useEffect } from 'react'
import { useLazyQuery, useMutation } from '@apollo/react-hooks'
import {
  CreateVoucherCostCategoryInput,
  CREATE_VOUCHER_COST_CATEGORY,
  GET_UPLOAD_LINK,
  DELETE_RECEIPT,
  GET_DOWNLOAD_LINK,
} from './gql'
import { VoucherReceipt } from '../../types'
import {
  Box,
  Button,
  styled,
  List,
  ListItem,
  Typography,
  IconButton,
  Tooltip,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile'
import DeleteIcon from '@mui/icons-material/Delete'
import FilePreviewButton from '../Common/FilePreviewButton'
import { toast } from 'react-toastify'
import VhsDialog from '../Common/VhsDialog'

type ReceiptsProps = {
  voucherId: string
  voucherCostCategory: any
  onCategoryChange: any
  voucherReceipts: VoucherReceipt[]
  paidByAmin: any
  readonly?: boolean
}
const Receipts: React.FunctionComponent<ReceiptsProps> = ({
  voucherId,
  voucherCostCategory,
  onCategoryChange,
  voucherReceipts,
  paidByAmin,
  readonly = false,
}) => {
  const [createVoucherCostCategory] = useMutation(CREATE_VOUCHER_COST_CATEGORY)
  const [deleteReceipt] = useMutation(DELETE_RECEIPT)
  const [getFileDownloadLink] = useLazyQuery(GET_DOWNLOAD_LINK, {
    onCompleted: (data) => {
      if (data?.signedDownloadLink?.signedLink) {
        window.open(data.signedDownloadLink.signedLink, '_blank')
      }
    },
  })
  const [vCostCategoryId, setVCostCategoryId] = useState(voucherCostCategory.id)
  const [receipts, setReceipts] = useState<Array<VoucherReceipt>>([])
  const [receiptToDelete, setReceiptToDelete] = useState<VoucherReceipt | null>(
    null,
  )

  useEffect(() => {
    setReceipts(voucherReceipts)
  }, [voucherReceipts])

  const [file, setFile] = useState<File | null>(null)
  const [getFileUploadLink] = useMutation(GET_UPLOAD_LINK, {
    onCompleted: async (uploadLinkData) => {
      await uploadFile(file, uploadLinkData.getUploadLink.signedLink)
      if (file) {
        const newReceipts = [
          {
            id: uploadLinkData.getUploadLink.receiptId,
            voucherId,
            path: file.name,
          },
          ...receipts,
        ]

        setReceipts(newReceipts)
        onCategoryChange(voucherCostCategory.id, { receipts: newReceipts })
      }
    },
  })

  async function uploadFile(fileObject, presignedUrl) {
    const requestOptions = {
      method: 'PUT',
      headers: {
        'Content-Type': fileObject.type,
      },
      body: fileObject,
    }
    const response = await fetch(presignedUrl, requestOptions)
    return response
  }

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const selectedFile = e.target.files[0]

      const isDuplicate = receipts.some((r) => r.path === selectedFile.name)
      if (isDuplicate) {
        toast.error(
          `"${selectedFile.name}" is already attached to this category.`,
        )
        e.target.value = ''
        return
      }

      setFile(selectedFile)

      getFileUploadLink({
        variables: {
          input: {
            fileName: selectedFile.name,
            voucherCostCategoryId: voucherCostCategory.id,
            voucherId,
          },
        },
      })

      e.target.value = ''
    }
  }

  const handleDeleteReceipt = async (receipt: VoucherReceipt) => {
    try {
      await deleteReceipt({
        variables: { id: receipt.id },
      })
      const newReceipts = receipts.filter((r) => r.id !== receipt.id)
      setReceipts(newReceipts)
      onCategoryChange(voucherCostCategory.id, { receipts: newReceipts })
    } catch (error: any) {
      console.error('Error deleting receipt:', error)
      toast.error(
        error.message ||
          `Receipt could not be deleted for voucher with id ${receipt.voucherId}`,
      )
    }
  }

  return (
    <Box>
      {receipts.length === 0 ? (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontStyle: 'italic', mb: 1 }}
        >
          No receipts attached yet.
        </Typography>
      ) : (
        <List dense disablePadding sx={{ mb: 1 }}>
          {receipts.map((r: any) => (
            <ListItem
              key={r.id}
              disableGutters
              disablePadding
              sx={{
                alignItems: 'center',
                gap: 0.5,
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                mb: 0.5,
                pr: 1,
              }}
            >
              <ListItemButton
                disableGutters
                sx={{ px: 1, borderRadius: 1, flex: 1, minWidth: 0 }}
                onClick={() => getFileDownloadLink({ variables: { id: r.id } })}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <InsertDriveFileIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={r.path}
                  primaryTypographyProps={{ noWrap: true, title: r.path }}
                />
              </ListItemButton>
              {!readonly && (
                <Tooltip title="Delete receipt">
                  <IconButton
                    aria-label="delete"
                    onClick={() => setReceiptToDelete(r)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </ListItem>
          ))}
        </List>
      )}

      {!readonly && (
        <Button
          component="label"
          variant="outlined"
          fullWidth
          startIcon={<CloudUploadIcon />}
        >
          Upload receipt
          <VisuallyHiddenInput type="file" onChange={handleFileChange} />
        </Button>
      )}

      <VhsDialog
        open={!!receiptToDelete}
        title="Delete Receipt"
        warningMessage={`Are you sure you want to delete this receipt ${receiptToDelete?.path}?`}
        onClose={() => setReceiptToDelete(null)}
        onConfirm={() => {
          if (receiptToDelete) {
            handleDeleteReceipt(receiptToDelete)
          }
          setReceiptToDelete(null)
        }}
        showReasonField={false}
        color="error"
        confirmButtonText="Delete"
      />
    </Box>
  )
}

export default Receipts

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
})
